// src/components/sdk/AppDetailModal.tsx
import React from "react";
import { SdkAppData } from "./SdkInterface";
import { Button, Descriptions, Modal, Tag, Typography, Popconfirm, message, InputNumber, Input, DatePicker, Switch, Popover } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { storageUtil } from "./utils/StorageUtil";
import dayjs from 'dayjs';
import { sdkMgmt } from "../../server/SdkMgmt";

const { Text } = Typography;

// 应用详情组件
export class AppDetailModal extends React.Component<{
    visible: boolean;
    app: SdkAppData | null;
    loading: boolean;
    onClose: () => void;
    t: (key: string, options?: any) => string;
    onQuotaIncrease?: (appId: number, priceId: number, addQuota: number) => Promise<any>;
    onUpdateApp?: (app: SdkAppData) => void;
    onRefreshList?: () => void;
    isMobile?: boolean;
}, {
    localTotalQuota: number;
    localRemainingQuota: number;
    localEndTime: string;
    localUnlimited: boolean;
    showSwitchPopover: boolean;
    switchTempQuota: number;
}> {
    constructor(props: any) {
        super(props);
        this.state = {
            localTotalQuota: props.app?.totalQuota || 0,
            localRemainingQuota: props.app?.remainingQuota || 0,
            localEndTime: props.app?.endTime || '',
            localUnlimited: props.app?.unlimited === 1,
            showSwitchPopover: false,
            switchTempQuota: 0,
        };
    }

    componentDidUpdate(prevProps: any) {
        const prevApp = prevProps.app;
        const currentApp = this.props.app;
        if (prevApp?.totalQuota !== currentApp?.totalQuota ||
            prevApp?.remainingQuota !== currentApp?.remainingQuota ||
            prevApp?.endTime !== currentApp?.endTime ||
            prevApp?.unlimited !== currentApp?.unlimited) {
            this.setState({
                localTotalQuota: currentApp?.totalQuota || 0,
                localRemainingQuota: currentApp?.remainingQuota || 0,
                localEndTime: currentApp?.endTime || '',
                localUnlimited: currentApp?.unlimited === 1,
            });
        }
    }

    hasChanges = (): boolean => {
        const { app } = this.props;
        const { localTotalQuota, localEndTime, localUnlimited } = this.state;
        if (!app) return false;

        const originalTotalQuota = app.totalQuota || 0;
        const originalEndTime = app.endTime || '';
        const originalUnlimited = app.unlimited === 1;

        return localTotalQuota !== originalTotalQuota ||
            localEndTime !== originalEndTime ||
            localUnlimited !== originalUnlimited;
    };

    handleIncreaseQuota = async () => {
        const { app, onQuotaIncrease } = this.props;
        if (!app) return;

        try {
            if (onQuotaIncrease) {
                let responseData = await onQuotaIncrease(app.appId, 1, 100);
                if (responseData) {
                    const totalQuota = responseData.totalQuota;
                    const remainingQuota = responseData.remainingQuota;
                    this.setState({
                        localTotalQuota: totalQuota,
                        localRemainingQuota: remainingQuota,
                    });
                    this.props.onUpdateApp?.({
                        ...app,
                        totalQuota: totalQuota,
                        remainingQuota: remainingQuota,
                    });
                }
                message.success(this.props.t('sdk.quota.increaseSuccess') || '增加次数成功！');
            } else {
                message.warning(this.props.t('sdk.quota.noHandler') || '未配置增加次数功能');
            }
        } catch (error) {
            message.error(this.props.t('sdk.quota.increaseFailed') || '增加次数失败，请重试');
            console.error('Increase quota failed:', error);
        }
    };

    handleQuotaChange = (value: number | null) => {
        const { app } = this.props;
        if (!app || value === null || value < 0) return;

        const usedQuota = app.usedQuota || 0;
        const newTotalQuota = Math.max(usedQuota, value);
        const newRemainingQuota = newTotalQuota - usedQuota;

        this.setState({
            localTotalQuota: newTotalQuota,
            localRemainingQuota: newRemainingQuota,
        });
    };

    handleEndTimeChange = (date: any, dateString: string | string[]) => {
        const newEndTime = typeof dateString === 'string' ? dateString : '';
        this.setState({
            localEndTime: newEndTime
        });
    };

    // 处理从有限切换到无限
    handleSwitchToUnlimited = () => {
        const { app } = this.props;
        if (!app) return;

        Modal.confirm({
            title: this.props.t('sdk.detail.switchToUnlimitedConfirm') || '确认切换到无限制',
            content: this.props.t('sdk.detail.switchToUnlimitedContent') || '确定要切换到无限制吗？切换后总次数将不再显示可编辑的数值输入框。',
            okText: this.props.t('sdk.detail.confirm') || '确认',
            cancelText: this.props.t('sdk.detail.cancel') || '取消',
            onOk: () => {
                this.setState({
                    localUnlimited: true,
                    localTotalQuota: 0,
                    localRemainingQuota: 0,
                });
                message.success(this.props.t('sdk.detail.switchToUnlimitedSuccess') || '已切换到无限制模式');
            },
        });
    };

    // 处理从无限切换到有限
    handleSwitchToLimited = () => {
        const { app } = this.props;
        if (!app) return;

        const usedQuota = app.usedQuota || 0;

        Modal.confirm({
            title: this.props.t('sdk.detail.switchToLimitedTitle') || '切换到有限模式',
            content: (
                <div>
                    <p>{this.props.t('sdk.detail.switchToLimitedDesc') || '请设置剩余调用次数：'}</p>
                    <InputNumber
                        value={this.state.switchTempQuota}
                        onChange={(value) => this.setState({ switchTempQuota: value || 0 })}
                        min={0}
                        precision={0}
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder={this.props.t('sdk.detail.enterRemainingQuota') || '请输入剩余次数'}
                        autoFocus
                    />
                </div>
            ),
            okText: this.props.t('sdk.detail.confirm') || '确认',
            cancelText: this.props.t('sdk.detail.cancel') || '取消',
            onOk: () => {
                const newRemainingQuota = this.state.switchTempQuota || 0;
                const newTotalQuota = newRemainingQuota + usedQuota;
                this.setState({
                    localUnlimited: false,
                    localTotalQuota: newTotalQuota,
                    localRemainingQuota: newRemainingQuota,
                    switchTempQuota: 0,
                });
                message.success(this.props.t('sdk.detail.switchToLimitedSuccess') || '已切换到有限模式');
            },
            onCancel: () => {
                this.setState({ switchTempQuota: 0 });
            },
        });
    };

    // 处理开关变化
    handleUnlimitedSwitchChange = (checked: boolean) => {
        if (checked) {
            this.handleSwitchToUnlimited();
        } else {
            this.handleSwitchToLimited();
        }
    };

    handleSubmit = async () => {
        const DEBUG = true;
        const TAG = `AppDetailModal.handleSubmit() - `;
        const { app, onUpdateApp, onClose, onRefreshList } = this.props;
        if (!app) return;

        const { localTotalQuota, localEndTime, localUnlimited } = this.state;
        const originalTotalQuota = app.totalQuota || 0;
        const usedQuota = app.usedQuota || 0;
        let quotaDelta = localTotalQuota - originalTotalQuota;
        DEBUG && console.log(TAG, `app.unlimited:`, app.unlimited);
        if (app.unlimited) {
            quotaDelta = localTotalQuota - usedQuota;
        }
        DEBUG && console.log(TAG, `quotaDelta:`, quotaDelta)
        const newRemainingQuota = localTotalQuota - usedQuota;

        try {
            const response = await sdkMgmt.updateApp({
                appId: app.appId,
                quotaDelta: quotaDelta,
                endTime: localEndTime,
                unlimited: localUnlimited ? 1 : 0,
            });

            if (response.success) {
                onUpdateApp?.({
                    ...app,
                    totalQuota: localTotalQuota,
                    remainingQuota: newRemainingQuota,
                    endTime: localEndTime,
                    unlimited: localUnlimited ? 1 : 0,
                });

                message.success(this.props.t('sdk.detail.saveSuccess') || '保存成功！');
                onClose?.();
                onRefreshList?.();
            } else {
                message.error(response.message || this.props.t('sdk.detail.saveFailed') || '保存失败！');
            }
        } catch (error) {
            message.error(this.props.t('sdk.detail.saveFailed') || '保存失败，请重试！');
            console.error('Update app failed:', error);
        }
    };

    // 移动端自定义渲染字段
    renderMobileFields() {
        const { app, t, isMobile = false } = this.props;
        const { localUnlimited, localTotalQuota, localRemainingQuota, localEndTime } = this.state;
        if (!app) return null;

        const isAdmin = storageUtil.isAdmin();
        const isGuest = storageUtil.isGuest();

        const statusMap: Record<string, { color: string; text: string }> = {
            active: { color: 'success', text: t('sdk.status.active') || '运行中' },
            inactive: { color: 'error', text: t('sdk.status.inactive') || '已停用' },
            pending: { color: 'warning', text: t('sdk.status.pending') || '审核中' },
        };
        const statusInfo = statusMap[app.status] || { color: 'default', text: app.status };

        const platformMap: Record<string, { color: string; text: string }> = {
            android: { color: 'green', text: t('sdk.platform.android') || 'Android' },
            ios: { color: 'blue', text: t('sdk.platform.ios') || 'iOS' },
        };
        const platformInfo = platformMap[app.platform] || { color: 'default', text: app.platform };

        const renderField = (label: string, content: React.ReactNode) => (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: '#fff',
            }}>
                <div style={{
                    fontSize: 13,
                    color: '#999',
                    marginBottom: 6,
                    fontWeight: 500,
                }}>
                    {label}
                </div>
                <div style={{
                    fontSize: 15,
                    wordBreak: 'break-all',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                }}>
                    {content}
                </div>
            </div>
        );

        const fields = [];

        // 应用ID
        fields.push(renderField(t('sdk.detail.appId') || '应用ID', app.appId));

        // 应用名称
        fields.push(renderField(t('sdk.detail.appName') || '应用名称', app.appName));

        // 平台
        fields.push(renderField(t('sdk.detail.platform') || '平台',
            <Tag color={platformInfo.color}>{platformInfo.text}</Tag>
        ));

        // 状态
        fields.push(renderField(t('sdk.detail.status') || '状态',
            <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
        ));

        // 创建时间
        fields.push(renderField(t('sdk.detail.createTime') || '创建时间', app.createTime));

        // 过期时间
        if (isAdmin) {
            const minDate = app.createTime ? dayjs(app.createTime) : undefined;
            fields.push(renderField(t('sdk.detail.endTime') || '过期时间',
                <DatePicker
                    value={localEndTime ? dayjs(localEndTime) : null}
                    onChange={this.handleEndTimeChange}
                    placeholder={t('sdk.detail.endTimePlaceholder') || '请选择或输入过期时间'}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD HH:mm:ss"
                    showTime={{ format: 'HH:mm:ss' }}
                    allowClear
                    showNow
                    size="middle"
                    disabledDate={(current) => {
                        if (minDate) {
                            return current && current.isBefore(minDate, 'day');
                        }
                        return false;
                    }}
                />
            ));
        } else {
            fields.push(renderField(t('sdk.detail.endTime') || '过期时间',
                app.endTime || t('sdk.detail.noExpiry') || '永不过期'
            ));
        }

        // 包名
        fields.push(renderField(t('sdk.detail.packageName') || '包名', app.packageName));

        // 总次数
        if (isAdmin) {
            fields.push(renderField(t('sdk.detail.totalQuota') || '总次数',
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {localUnlimited ? (
                        <Tag color="green">{t('sdk.detail.unlimited') || '无限制'}</Tag>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Button
                                size="middle"
                                onClick={() => {
                                    const usedQuota = app.usedQuota || 0;
                                    const newValue = Math.max(usedQuota, (localTotalQuota || 0) - 1);
                                    this.handleQuotaChange(newValue);
                                }}
                                disabled={(localTotalQuota || 0) <= (app.usedQuota || 0)}
                                style={{ padding: '0 10px', minWidth: 28, height: 32 }}
                            >
                                -
                            </Button>
                            <InputNumber
                                value={localTotalQuota}
                                onChange={(value) => this.handleQuotaChange(value)}
                                min={app.usedQuota || 0}
                                precision={0}
                                style={{ width: 70, textAlign: 'center' }}
                                controls={false}
                                size="middle"
                            />
                            <Button
                                size="middle"
                                onClick={() => {
                                    const newValue = (localTotalQuota || 0) + 1;
                                    this.handleQuotaChange(newValue);
                                }}
                                style={{ padding: '0 10px', minWidth: 28, height: 32 }}
                            >
                                +
                            </Button>
                        </div>
                    )}
                    <Switch
                        checked={localUnlimited}
                        onChange={this.handleUnlimitedSwitchChange}
                        checkedChildren="无限"
                        unCheckedChildren="有限"
                        size="default"
                    />
                </div>
            ));
        } else {
            if (localUnlimited) {
                fields.push(renderField(t('sdk.detail.totalQuota') || '总次数',
                    <Tag color="green">{t('sdk.detail.unlimited') || '无限制'}</Tag>
                ));
            } else {
                fields.push(renderField(t('sdk.detail.totalQuota') || '总次数',
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{app.totalQuota}</span>
                        {!isGuest && this.renderIncreaseBtn()}
                    </div>
                ));
            }
        }

        // 已用次数
        fields.push(renderField(t('sdk.detail.usedQuota') || '已用次数', app.usedQuota));

        // 剩余次数
        if (localUnlimited) {
            fields.push(renderField(t('sdk.detail.remainingQuota') || '剩余调用次数',
                <Tag color="green">{t('sdk.detail.unlimited') || '无限制'}</Tag>
            ));
        } else {
            fields.push(renderField(t('sdk.detail.remainingQuota') || '剩余调用次数', localRemainingQuota));
        }

        // App Key
        fields.push(renderField(t('sdk.detail.appKey') || 'App Key',
            <Text copyable style={{ wordBreak: 'break-all' }}>{app.appKey}</Text>
        ));

        // App Secret
        fields.push(renderField(t('sdk.detail.appSecret') || 'App Secret',
            <Text copyable style={{ wordBreak: 'break-all' }}>{app.appSecret}</Text>
        ));

        // 描述
        if (app.description) {
            fields.push(renderField(t('sdk.detail.description') || '描述', app.description));
        }

        return <div style={{ padding: '0', backgroundColor: '#fff' }}>{fields}</div>;
    }

    render() {
        const { visible, app, loading, onClose, t, isMobile = false } = this.props;
        const hasChanges = this.hasChanges();

        if (!app) return null;

        const platformMap: Record<string, { color: string; text: string }> = {
            android: { color: 'green', text: t('sdk.platform.android') || 'Android' },
            ios: { color: 'blue', text: t('sdk.platform.ios') || 'iOS' },
        };
        const platformInfo = platformMap[app.platform] || { color: 'default', text: app.platform };

        const statusMap: Record<string, { color: string; text: string }> = {
            active: { color: 'success', text: t('sdk.status.active') || '运行中' },
            inactive: { color: 'error', text: t('sdk.status.inactive') || '已停用' },
            pending: { color: 'warning', text: t('sdk.status.pending') || '审核中' },
        };
        const statusInfo = statusMap[app.status] || { color: 'default', text: app.status };

        const footerButtons = [];

        if (storageUtil.isAdmin() && hasChanges) {
            footerButtons.push(
                <Button key="submit" type="primary" onClick={this.handleSubmit} loading={loading} size={isMobile ? 'small' : 'middle'}>
                    {t('sdk.detail.submit') || '提交'}
                </Button>
            );
        }

        footerButtons.push(
            <Button key="close" onClick={onClose} size={isMobile ? 'small' : 'middle'}>
                {t('sdk.detail.close') || '关闭'}
            </Button>
        );

        // 移动端使用自定义渲染
        if (isMobile) {
            return (
                <Modal
                    title={t('sdk.detail.title') || '应用详情'}
                    open={visible}
                    onCancel={onClose}
                    footer={footerButtons}
                    width="100%"
                    style={{
                        top: 0,
                        maxWidth: '100%',
                        margin: 0,
                        padding: 0,
                    }}
                    bodyStyle={{
                        padding: 0,
                        maxHeight: 'calc(100vh - 120px)',
                        overflowY: 'auto',
                        height: 'calc(100vh - 120px)',
                    }}
                    maskClosable={false}
                    closable={true}
                    destroyOnClose
                >
                    {this.renderMobileFields()}
                </Modal>
            );
        }

        // 桌面端使用 Descriptions
        return (
            <Modal
                title={t('sdk.detail.title') || '应用详情'}
                open={visible}
                onCancel={onClose}
                footer={footerButtons}
                width="50%"
                confirmLoading={loading}
            >
                <Descriptions column={2} bordered>
                    <Descriptions.Item label={t('sdk.detail.appId') || '应用ID'}>
                        {app.appId}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('sdk.detail.appName') || '应用名称'}>
                        {app.appName}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('sdk.detail.platform') || '平台'}>
                        <Tag color={platformInfo.color}>{platformInfo.text}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('sdk.detail.status') || '状态'}>
                        <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('sdk.detail.createTime') || '创建时间'}>
                        {app.createTime}
                    </Descriptions.Item>
                    {this.renderExpiryTime()}
                    <Descriptions.Item label={t('sdk.detail.packageName') || '包名'}>
                        {app.packageName}
                    </Descriptions.Item>
                    {this.renderTotalQuota()}
                    <Descriptions.Item label={t('sdk.detail.usedQuota') || '已用次数'}>
                        {app.usedQuota}
                    </Descriptions.Item>
                    {this.renderRemainingQuota()}
                    <Descriptions.Item label={t('sdk.detail.appKey') || 'App Key'} span={2}>
                        <Text copyable>{app.appKey}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('sdk.detail.appSecret') || 'App Secret'} span={2}>
                        <Text copyable>{app.appSecret}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('sdk.detail.description') || '描述'} span={2}>
                        {app.description}
                    </Descriptions.Item>
                </Descriptions>
            </Modal>
        );
    }

    /** 渲染过期时间（桌面端） */
    renderExpiryTime() {
        const { app, t } = this.props;
        const { localEndTime } = this.state;
        if (!app) return null;

        const isAdmin = storageUtil.isAdmin();
        const createTime = app.createTime || '';

        if (isAdmin) {
            const minDate = createTime ? dayjs(createTime) : undefined;

            return (
                <Descriptions.Item label={t('sdk.detail.endTime') || '过期时间'}>
                    <DatePicker
                        value={localEndTime ? dayjs(localEndTime) : null}
                        onChange={this.handleEndTimeChange}
                        placeholder={t('sdk.detail.endTimePlaceholder') || '请选择或输入过期时间'}
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD HH:mm:ss"
                        showTime={{ format: 'HH:mm:ss' }}
                        allowClear
                        showNow
                    />
                </Descriptions.Item>
            );
        }
        else {
            return (
                <Descriptions.Item label={t('sdk.detail.endTime') || '过期时间'}>
                    {app.endTime || t('sdk.detail.noExpiry') || '永不过期'}
                </Descriptions.Item>
            );
        }
    }

    /** 渲染总次数（桌面端） */
    renderTotalQuota() {
        const { app, t } = this.props;
        const { localTotalQuota, localUnlimited } = this.state;
        if (!app) return null;

        const isAdmin = storageUtil.isAdmin();

        if (!isAdmin) {
            if (localUnlimited) {
                return (
                    <Descriptions.Item label={t('sdk.detail.totalQuota') || '总次数'} span={2}>
                        <Tag color="green">{t('sdk.detail.unlimited') || '无限制'}</Tag>
                    </Descriptions.Item>
                );
            }
            return (
                <Descriptions.Item label={t('sdk.detail.totalQuota') || '总次数'} span={2}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{app.totalQuota}</span>
                        {this.renderIncreaseBtn()}
                    </div>
                </Descriptions.Item>
            );
        }

        return (
            <Descriptions.Item label={t('sdk.detail.totalQuota') || '总次数'} span={2}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {localUnlimited ? (
                        <Tag color="green">{t('sdk.detail.unlimited') || '无限制'}</Tag>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Button
                                size="small"
                                onClick={() => {
                                    const usedQuota = app.usedQuota || 0;
                                    const newValue = Math.max(usedQuota, (localTotalQuota || 0) - 1);
                                    this.handleQuotaChange(newValue);
                                }}
                                disabled={(localTotalQuota || 0) <= (app.usedQuota || 0)}
                            >
                                -
                            </Button>
                            <InputNumber
                                value={localTotalQuota}
                                onChange={(value) => this.handleQuotaChange(value)}
                                min={app.usedQuota || 0}
                                precision={0}
                                style={{ width: 80, textAlign: 'center' }}
                                controls={false}
                            />
                            <Button
                                size="small"
                                onClick={() => {
                                    const newValue = (localTotalQuota || 0) + 1;
                                    this.handleQuotaChange(newValue);
                                }}
                            >
                                +
                            </Button>
                        </div>
                    )}
                    <Switch
                        checked={localUnlimited}
                        onChange={this.handleUnlimitedSwitchChange}
                        checkedChildren={t('sdk.detail.unlimited') || '无限制'}
                        unCheckedChildren={t('sdk.detail.limited') || '有限'}
                    />
                </div>
            </Descriptions.Item>
        );
    }

    /** 渲染剩余次数（桌面端） */
    renderRemainingQuota() {
        const { app, t } = this.props;
        const { localRemainingQuota, localUnlimited } = this.state;
        if (!app) return null;

        if (localUnlimited) {
            return (
                <Descriptions.Item label={t('sdk.detail.remainingQuota') || '剩余调用次数'}>
                    <Tag color="green">{t('sdk.detail.unlimited') || '无限制'}</Tag>
                </Descriptions.Item>
            );
        }
        return (
            <Descriptions.Item label={t('sdk.detail.remainingQuota') || '剩余调用次数'}>
                {localRemainingQuota}
            </Descriptions.Item>
        );
    }

    renderIncreaseBtn() {
        const { t, isMobile = false } = this.props;

        const isGuest = storageUtil.isGuest();
        if (isGuest) {
            return null;
        }
        return (
            <Popconfirm
                title={t('sdk.quota.confirmTitle') || '确认增加次数'}
                description={t('sdk.quota.confirmDescription') || '确定要增加该应用的调用次数吗？'}
                onConfirm={this.handleIncreaseQuota}
                okText={t('sdk.quota.confirmOk') || '确认'}
                cancelText={t('sdk.quota.confirmCancel') || '取消'}
            >
                <Button
                    type="primary"
                    size={isMobile ? 'small' : 'small'}
                    icon={<PlusOutlined />}
                >
                    {t('sdk.quota.increase') || '增加次数'}
                </Button>
            </Popconfirm>
        )
    }
}